<template>
  <CMSStory
    v-if="resolvedData && status === 'success'"
    v-bind="$attrs"
    :story="resolvedData"
  />
  <slot v-else-if="status === 'pending' || status === 'idle'" name="loading" />
  <slot v-else-if="status === 'error'" name="error">
    <slot name="loading" />
  </slot>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  useAvailableShops,
  useCurrentShop,
} from '@scayle/storefront-nuxt/composables'
import type {
  TypeContentPageSkeleton,
  TypeContentPageWithoutUnresolvableLinksResponse,
} from '../types'
import { useCMSBySlug } from '../composables/useCMS'
import { useContentfulEditor } from '../composables/useContentfulEditor'
import CMSStory from './Story.vue'
import { createError } from '#imports'

const { slug } = defineProps<{ slug: string }>()

const currentShop = useCurrentShop()
const availableShops = useAvailableShops()

const currentShopLocale = computed(() => {
  return (
    availableShops.value.find(
      (shop) => shop.locale === currentShop.value.locale,
    )?.locale ?? 'en-GB'
  )
})

const { data, status } = useCMSBySlug<TypeContentPageSkeleton>(
  `content-page-${slug}`,
  {
    content_type: 'contentPage',
    'fields.slug': `${slug}`,
    // @ts-expect-error wrong type definition in useCMSBySlug from core
    locale: currentShopLocale.value,
  },
)

useContentfulEditor<TypeContentPageSkeleton>(data)

const resolvedData = computed(() => {
  const res = data.value as TypeContentPageWithoutUnresolvableLinksResponse

  // Check if content exists, if not throw 404
  if (!res || !res.fields) {
    if (status.value === 'success') {
      // Data was fetched successfully but content doesn't exist
      throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
      })
    }
    // Return null while loading or on error
    return null
  }

  return {
    ...res,
    uuid: res.fields.uid ?? '',
    _uid: res.fields.uid ?? '',
    slug: res.fields.slug ?? '',
    teaser_image: res.fields.teaserImage ?? '',
    teaser_image_mobile: res.fields.teaserImageMobile ?? '',
    headline: res.fields.headline ?? '',
    subline: res.fields.subline ?? '',
    content: res.fields.content ?? [],
    name: res.fields.headline ?? '',
    seo: res.fields.seo ?? {},
    bg_color: res.fields.backgroundColor ?? '',
  }
})

defineOptions({ name: 'CMSContentPage', inheritAttrs: false })
</script>
